import { useState, useCallback } from 'react';
import type {
    AxleBlueprint,
    AxleConfig,
    TireSlot,
    TireGraphicType,
    TireGraphicDef,
} from '../types/axleBlueprint';
import { TIRE_CATEGORIES } from '../utils/constants';
import '../css/AxleBlueprintBuilder.css';

// ── Tire graphic presets ──
export const TIRE_GRAPHICS: Record<TireGraphicType, TireGraphicDef> = {
    'construction': { type: 'construction', label: 'A', displayName: 'Construction', width: 44, height: 74, category: 'Construction' },
    'farm': { type: 'farm', label: 'B', displayName: 'Farm - Ag', width: 40, height: 68, category: 'Farm - Ag' },
    'commercial': { type: 'commercial', label: 'C', displayName: 'Commercial', width: 34, height: 64, category: 'Commercial - Truck - Trailer - Bus' },
    'light-truck': { type: 'light-truck', label: 'D', displayName: 'Auto / Light Truck', width: 30, height: 54, category: 'Auto - LT - SUV - Trailer' },
    'atv': { type: 'atv', label: 'E', displayName: 'ATV / Lawn & Garden', width: 38, height: 42, category: 'ATV - UTV - Lawn & Garden' },
    'motorcycle': { type: 'motorcycle', label: 'F', displayName: 'Motorcycle', width: 20, height: 54, category: 'Motorcycle' },
    'aviation': { type: 'aviation', label: 'G', displayName: 'Aviation', width: 50, height: 64, category: 'Aviation' },
    'military': { type: 'military', label: 'H', displayName: 'Military', width: 56, height: 68, category: 'Military - Land' },
};

const MAX_AXLES = 8;
const MAX_TIRES_PER_SIDE = 6;

let _nextId = 1;
const uid = () => `slot-${Date.now()}-${_nextId++}`;

// ── Helper: generate position code like LF1, RF2, CRM1 ──
function makePositionCode(axleLabel: string, side: 'L' | 'R' | 'C', index: number): string {
    // Take first letter of each word in axle label, e.g. "Front Axle" -> "F", "Front Middle Axle" -> "FM"
    const words = axleLabel.split(/\s+/).filter(w => w.toLowerCase() !== 'axle');
    const prefix = words.map(w => w.charAt(0).toUpperCase()).join('');
    return `${side}${prefix}${index}`;
}

// ── Helper: rebuild tire slots for an axle based on tiresPerSide + singleWheel ──
function rebuildSlots(axle: AxleConfig, defaultGraphic: TireGraphicType = 'commercial'): TireSlot[] {
    const slots: TireSlot[] = [];
    const halfWidth = axle.width / 2;

    // Single wheel = center only, no left/right
    if (axle.singleWheel) {
        const existing = axle.tireSlots.find(s => s.positionCode === makePositionCode(axle.label, 'C', 1));
        slots.push({
            id: existing?.id || uid(),
            positionCode: makePositionCode(axle.label, 'C', 1),
            xOffset: 0,
            tireGraphic: existing?.tireGraphic || defaultGraphic,
            assignedTireId: existing?.assignedTireId,
        });
        return slots;
    }

    // Position 1 = outermost (furthest from center) on both sides.
    // Left side slots
    const spacing = halfWidth / (axle.tiresPerSide + 1);
    for (let i = 0; i < axle.tiresPerSide; i++) {
        const posNum = i + 1; // 1 = outermost
        const existing = axle.tireSlots.find(s => s.positionCode === makePositionCode(axle.label, 'L', posNum));
        slots.push({
            id: existing?.id || uid(),
            positionCode: makePositionCode(axle.label, 'L', posNum),
            xOffset: -(spacing * (axle.tiresPerSide - i)),
            tireGraphic: existing?.tireGraphic || defaultGraphic,
            assignedTireId: existing?.assignedTireId,
        });
    }

    // Odd wheel = add a center tire between left and right
    if (axle.oddWheel) {
        const existing = axle.tireSlots.find(s => s.positionCode === makePositionCode(axle.label, 'C', 1));
        slots.push({
            id: existing?.id || uid(),
            positionCode: makePositionCode(axle.label, 'C', 1),
            xOffset: 0,
            tireGraphic: existing?.tireGraphic || defaultGraphic,
            assignedTireId: existing?.assignedTireId,
        });
    }

    // Right side slots — mirror left: position 1 = outermost (largest xOffset)
    for (let i = 0; i < axle.tiresPerSide; i++) {
        const posNum = i + 1; // 1 = outermost
        const existing = axle.tireSlots.find(s => s.positionCode === makePositionCode(axle.label, 'R', posNum));
        slots.push({
            id: existing?.id || uid(),
            positionCode: makePositionCode(axle.label, 'R', posNum),
            xOffset: spacing * (axle.tiresPerSide - i),
            tireGraphic: existing?.tireGraphic || defaultGraphic,
            assignedTireId: existing?.assignedTireId,
        });
    }

    return slots;
}

interface Props {
    initialBlueprint?: AxleBlueprint | null;
    onSave: (blueprint: AxleBlueprint) => void;
    onCancel: () => void;
    companyId: string;
    assetId?: string;
}

export const AxleBlueprintBuilder = ({ initialBlueprint, onSave, onCancel, companyId, assetId }: Props) => {
    const [name, setName] = useState(initialBlueprint?.name || '');
    const [description, setDescription] = useState(initialBlueprint?.description || '');
    const [axles, setAxles] = useState<AxleConfig[]>(initialBlueprint?.axles || []);
    const [selectedAxleId, setSelectedAxleId] = useState<string | null>(
        initialBlueprint?.axles?.[0]?.id || null
    );
    const [assignIndividually, setAssignIndividually] = useState(false);

    const selectedAxle = axles.find(a => a.id === selectedAxleId) || null;

    // When selecting an axle, auto-open individual mode if it has mixed categories
    const selectAxle = (axleId: string) => {
        setSelectedAxleId(axleId);
        const axle = axles.find(a => a.id === axleId);
        if (axle && !axle.tireCategory) {
            const cats = [...new Set(axle.tireSlots.map(s => TIRE_GRAPHICS[s.tireGraphic]?.category))];
            setAssignIndividually(cats.length > 1);
        } else {
            setAssignIndividually(false);
        }
    };

    // Get tire graphic options filtered by a category
    const graphicsForCategory = (category: string): TireGraphicType[] =>
        Object.values(TIRE_GRAPHICS).filter(g => g.category === category).map(g => g.type);

    // ── Add axle ──
    const addAxle = useCallback(() => {
        if (axles.length >= MAX_AXLES) return;
        const existingLabels = axles.map(a => a.label);

        let label: string;
        if (axles.length === 0) {
            label = 'Front Axle';
        } else {
            // Find next available rear number
            let rearNum = 1;
            while (existingLabels.includes(rearNum === 1 ? 'Rear Axle' : `Rear Axle ${rearNum}`)) {
                rearNum++;
            }
            label = rearNum === 1 ? 'Rear Axle' : `Rear Axle ${rearNum}`;
        }

        const newAxle: AxleConfig = {
            id: `axle-${Date.now()}-${axles.length + 1}`,
            label,
            yPosition: 0,
            width: 280,
            tiresPerSide: 1,
            singleWheel: false,
            oddWheel: false,
            tireSlots: [],
        };
        // Build initial slots
        newAxle.tireSlots = rebuildSlots(newAxle);
        setAxles(prev => [...prev, newAxle]);
        setSelectedAxleId(newAxle.id);
    }, [axles]);

    // ── Remove axle ──
    const removeAxle = (axleId: string) => {
        setAxles(prev => prev.filter(a => a.id !== axleId));
        if (selectedAxleId === axleId) {
            setSelectedAxleId(axles.find(a => a.id !== axleId)?.id || null);
        }
    };

    // ── Update axle label ──
    const updateAxleLabel = (axleId: string, label: string) => {
        setAxles(prev => prev.map(a => {
            if (a.id !== axleId) return a;
            const updated = { ...a, label };
            return { ...updated, tireSlots: rebuildSlots(updated, a.tireSlots[0]?.tireGraphic) };
        }));
    };

    // ── Change tires per side ──
    const setTiresPerSide = (axleId: string, count: number) => {
        const clamped = Math.max(1, Math.min(MAX_TIRES_PER_SIDE, count));
        setAxles(prev => prev.map(a => {
            if (a.id !== axleId) return a;
            const updated = { ...a, tiresPerSide: clamped };
            return { ...updated, tireSlots: rebuildSlots(updated, a.tireSlots[0]?.tireGraphic) };
        }));
    };

    // ── Toggle single wheel ──
    const toggleSingleWheel = (axleId: string) => {
        setAxles(prev => prev.map(a => {
            if (a.id !== axleId) return a;
            const updated = { ...a, singleWheel: !a.singleWheel, oddWheel: false };
            return { ...updated, tireSlots: rebuildSlots(updated, a.tireSlots[0]?.tireGraphic) };
        }));
    };

    // ── Toggle odd wheel (center tire + left/right) ──
    const toggleOddWheel = (axleId: string) => {
        setAxles(prev => prev.map(a => {
            if (a.id !== axleId) return a;
            const updated = { ...a, oddWheel: !a.oddWheel, singleWheel: false };
            return { ...updated, tireSlots: rebuildSlots(updated, a.tireSlots[0]?.tireGraphic) };
        }));
    };

    // ── Set all slots on an axle to a category (uses first graphic in that category) ──
    const setAxleCategory = (axleId: string, category: string) => {
        setAxles(prev => prev.map(a => {
            if (a.id !== axleId) return a;
            if (!category) {
                return { ...a, tireCategory: undefined };
            }
            const graphics = graphicsForCategory(category);
            const defaultGraphic = graphics.length > 0 ? graphics[0] : a.tireSlots[0]?.tireGraphic || 'commercial';
            return {
                ...a,
                tireCategory: category,
                tireSlots: a.tireSlots.map(s => ({ ...s, tireGraphic: defaultGraphic })),
            };
        }));
    };

    // ── Change tire graphic for a slot ──
    const updateSlotGraphic = (axleId: string, slotId: string, graphic: TireGraphicType) => {
        setAxles(prev => prev.map(a => {
            if (a.id !== axleId) return a;
            return {
                ...a,
                tireSlots: a.tireSlots.map(s => s.id === slotId ? { ...s, tireGraphic: graphic } : s),
            };
        }));
    };

    // ── Summary calculations ──
    const totalSlots = axles.reduce((sum, a) => sum + a.tireSlots.length, 0);
    const assignedCount = axles.reduce((sum, a) => sum + a.tireSlots.filter(s => s.assignedTireId).length, 0);
    const drivenAxles = axles.filter(a => a.label.toLowerCase().includes('drive') || a.label.toLowerCase().includes('rear')).length;

    // ── Save ──
    const handleSave = () => {
        if (!name.trim()) return;
        // Recalculate yPositions evenly for storage
        const spacing = 400 / (axles.length + 1);
        const finalAxles = axles.map((a, i) => ({
            ...a,
            yPosition: spacing * (i + 1),
        }));
        const blueprint: AxleBlueprint = {
            ...(initialBlueprint?.id ? { id: initialBlueprint.id } : {}),
            name: name.trim(),
            description: description.trim() || undefined,
            companyId,
            centerlineLength: 400,
            axles: finalAxles,
        };
        onSave(blueprint);
    };

    return (
        <div className="axle-builder-overlay">
            <div className="axle-builder-modal">
                {/* Header */}
                <div className="ab-header">
                    <h2>Axle &amp; Tire Configuration</h2>
                    <button className="ab-close-btn" onClick={onCancel} aria-label="Close">×</button>
                </div>

                <div className="ab-body">
                    {/* Top fields */}
                    <div className="ab-top-fields">
                        <div className="ab-field">
                            <label className="ab-label">Asset ID</label>
                            <input className="ab-input" value={assetId || ''} readOnly />
                        </div>
                        <div className="ab-field">
                            <label className="ab-label">Configuration Name</label>
                            <input
                                className="ab-input"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="e.g. 12x4"
                            />
                        </div>
                        <div className="ab-field">
                            <label className="ab-label">Description</label>
                            <input
                                className="ab-input"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="e.g. 14-wheel heavy haul"
                            />
                        </div>
                    </div>

                    <div className="ab-split">
                        {/* ── Left: Axle Diagram ── */}
                        <div className="ab-diagram-col">
                            <h3 className="ab-section-title">Axle Diagram</h3>
                            <div className="ab-diagram">
                                {axles.map((axle, axleIdx) => {
                                    const leftSlots = axle.tireSlots.filter(s => s.xOffset < 0).sort((a, b) => a.xOffset - b.xOffset);
                                    const centerSlots = axle.tireSlots.filter(s => s.xOffset === 0);
                                    const rightSlots = axle.tireSlots.filter(s => s.xOffset > 0).sort((a, b) => a.xOffset - b.xOffset);
                                    const isSelected = selectedAxleId === axle.id;

                                    return (
                                        <div key={axle.id} className="ab-axle-group">
                                            <span className="ab-axle-label-top">{axle.label}</span>
                                            <div
                                                className={`ab-axle-row ${isSelected ? 'selected' : ''}`}
                                                onClick={() => selectAxle(axle.id)}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={`Select ${axle.label}`}
                                            >
                                                {/* Left tires */}
                                                <div className="ab-tire-group left">
                                                    {leftSlots.map(slot => (
                                                        <TireSlotBox
                                                            key={slot.id}
                                                            slot={slot}
                                                            isAxleSelected={isSelected}
                                                        />
                                                    ))}
                                                </div>

                                                {/* Center hub + optional center tire */}
                                                <div className="ab-hub-area">
                                                    {centerSlots.map(slot => (
                                                        <TireSlotBox
                                                            key={slot.id}
                                                            slot={slot}
                                                            isAxleSelected={isSelected}
                                                            isCenter
                                                        />
                                                    ))}
                                                    {centerSlots.length === 0 && <div className="ab-hub-dot" />}
                                                </div>

                                                {/* Right tires */}
                                                <div className="ab-tire-group right">
                                                    {rightSlots.map(slot => (
                                                        <TireSlotBox
                                                            key={slot.id}
                                                            slot={slot}
                                                            isAxleSelected={isSelected}
                                                        />
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Shaft connector */}
                                            {axleIdx < axles.length - 1 && <div className="ab-shaft" />}
                                        </div>
                                    );
                                })}

                                {axles.length === 0 && (
                                    <p className="ab-empty-msg">Add an axle to get started</p>
                                )}
                            </div>

                            {/* Summary */}
                            {axles.length > 0 && (
                                <div className="ab-summary">
                                    <h4>Summary</h4>
                                    <p className="ab-summary-name">{name || '—'}</p>
                                    <p className="ab-summary-detail">
                                        {totalSlots} wheel position{totalSlots !== 1 ? 's' : ''}
                                        {drivenAxles > 0 && `, ${drivenAxles} driven`}
                                    </p>
                                    <p className="ab-summary-detail">
                                        {axles.length} axle{axles.length !== 1 ? 's' : ''} · {totalSlots} total slots
                                    </p>
                                    <p className="ab-summary-detail">
                                        {assignedCount} tire{assignedCount !== 1 ? 's' : ''} assigned · {totalSlots - assignedCount} empty
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* ── Right: Controls ── */}
                        <div className="ab-controls-col">
                            {/* Axle list */}
                            <h3 className="ab-section-title">Axles</h3>
                            <div className="ab-axle-list">
                                {axles.map(axle => (
                                    <div
                                        key={axle.id}
                                        className={`ab-axle-list-item ${selectedAxleId === axle.id ? 'selected' : ''}`}
                                        onClick={() => selectAxle(axle.id)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <input
                                            className="ab-axle-name-input"
                                            value={axle.label}
                                            onChange={e => updateAxleLabel(axle.id, e.target.value)}
                                            onClick={e => e.stopPropagation()}
                                            placeholder="Axle name..."
                                        />
                                        <span className="ab-slot-badge">{axle.tireSlots.length} slots</span>
                                        {(() => {
                                            if (axle.tireCategory) {
                                                return <span className="ab-category-badge" title={`Category: ${axle.tireCategory}`}>✓</span>;
                                            }
                                            const cats = [...new Set(axle.tireSlots.map(s => TIRE_GRAPHICS[s.tireGraphic]?.category).filter(Boolean))];
                                            if (cats.length > 1) {
                                                return <span className="ab-category-badge partial" title={`Mixed: ${cats.join(', ')}`}>⚠</span>;
                                            }
                                            if (cats.length === 1) {
                                                return <span className="ab-category-badge" title={`Category: ${cats[0]}`}>✓</span>;
                                            }
                                            return <span className="ab-category-badge unset" title="Select this axle to assign a tire category below">!</span>;
                                        })()}
                                        <button
                                            className="ab-remove-btn"
                                            onClick={e => { e.stopPropagation(); removeAxle(axle.id); }}
                                            aria-label={`Remove ${axle.label}`}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                {axles.length < MAX_AXLES && (
                                    <button className="ab-add-axle-btn" onClick={addAxle}>
                                        + Add Axle
                                    </button>
                                )}
                            </div>

                            {/* Tires per side + single wheel */}
                            {selectedAxle && (
                                <>
                                    <div className="ab-tps-section">
                                        <h3 className="ab-section-title">Tires Per Side</h3>
                                        <p className="ab-editing-label">Editing: {selectedAxle.label}</p>
                                        <div className={`ab-counter ${selectedAxle.singleWheel ? 'disabled' : ''}`}>
                                            <button
                                                className="ab-counter-btn"
                                                onClick={() => setTiresPerSide(selectedAxle.id, selectedAxle.tiresPerSide - 1)}
                                                disabled={selectedAxle.singleWheel}
                                            >
                                                −
                                            </button>
                                            <span className="ab-counter-value">{selectedAxle.singleWheel ? '—' : selectedAxle.tiresPerSide}</span>
                                            <button
                                                className="ab-counter-btn"
                                                onClick={() => setTiresPerSide(selectedAxle.id, selectedAxle.tiresPerSide + 1)}
                                                disabled={selectedAxle.singleWheel}
                                            >
                                                +
                                            </button>
                                        </div>
                                        <span className="ab-counter-hint">{selectedAxle.singleWheel ? 'Disabled — single wheel active' : `Tires per side (1–${MAX_TIRES_PER_SIDE})`}</span>

                                        <div className="ab-checkbox-row">
                                            <label className="ab-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedAxle.singleWheel}
                                                    onChange={() => toggleSingleWheel(selectedAxle.id)}
                                                />
                                                Single Wheel
                                            </label>
                                            <label className="ab-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedAxle.oddWheel}
                                                    onChange={() => toggleOddWheel(selectedAxle.id)}
                                                    disabled={selectedAxle.singleWheel}
                                                />
                                                Odd Number Wheels
                                            </label>
                                        </div>
                                    </div>

                                    {/* Tire category assignment */}
                                    <div className="ab-slots-section">
                                        <h3 className="ab-section-title">Tire Category: {selectedAxle.label}</h3>

                                        <div className="ab-category-assign">
                                            <select
                                                className="ab-category-select"
                                                value={selectedAxle.tireCategory || ''}
                                                onChange={e => setAxleCategory(selectedAxle.id, e.target.value)}
                                                disabled={assignIndividually}
                                            >
                                                <option value="">Select Category</option>
                                                {Object.values(TIRE_CATEGORIES).map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                            <button
                                                className={`ab-individual-toggle ${assignIndividually ? 'active' : ''}`}
                                                onClick={() => {
                                                    const goingIndividual = !assignIndividually;
                                                    setAssignIndividually(goingIndividual);
                                                    if (goingIndividual && selectedAxle) {
                                                        // Clear axle-level category so per-slot values take over
                                                        setAxles(prev => prev.map(a =>
                                                            a.id === selectedAxle.id ? { ...a, tireCategory: undefined } : a
                                                        ));
                                                    }
                                                }}
                                            >
                                                {assignIndividually ? '← Apply to all' : 'Assign individually'}
                                            </button>
                                        </div>

                                        {assignIndividually && (
                                            <div className="ab-individual-slots">
                                                {/* Left side */}
                                                {selectedAxle.tireSlots.filter(s => s.xOffset < 0).length > 0 && (
                                                    <p className="ab-side-label">Left side</p>
                                                )}
                                                {selectedAxle.tireSlots
                                                    .filter(s => s.xOffset < 0)
                                                    .map((slot, i) => (
                                                        <SlotRow
                                                            key={slot.id}
                                                            slot={slot}
                                                            index={i + 1}
                                                            axleId={selectedAxle.id}
                                                            onChangeGraphic={updateSlotGraphic}
                                                        />
                                                    ))}

                                                {/* Center */}
                                                {(selectedAxle.singleWheel || selectedAxle.oddWheel) && (
                                                    <>
                                                        <p className="ab-side-label">Center</p>
                                                        {selectedAxle.tireSlots
                                                            .filter(s => s.xOffset === 0)
                                                            .map((slot, i) => (
                                                                <SlotRow
                                                                    key={slot.id}
                                                                    slot={slot}
                                                                    index={i + 1}
                                                                    axleId={selectedAxle.id}
                                                                    onChangeGraphic={updateSlotGraphic}
                                                                />
                                                            ))}
                                                    </>
                                                )}

                                                {/* Right side */}
                                                {selectedAxle.tireSlots.filter(s => s.xOffset > 0).length > 0 && (
                                                    <p className="ab-side-label">Right side</p>
                                                )}
                                                {selectedAxle.tireSlots
                                                    .filter(s => s.xOffset > 0)
                                                    .map((slot, i) => (
                                                        <SlotRow
                                                            key={slot.id}
                                                            slot={slot}
                                                            index={i + 1}
                                                            axleId={selectedAxle.id}
                                                            onChangeGraphic={updateSlotGraphic}
                                                        />
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="ab-footer">
                    <button className="ab-btn-cancel" onClick={onCancel}>Cancel</button>
                    <button className="ab-btn-save" onClick={handleSave} disabled={!name.trim()}>
                        Save Configuration
                    </button>
                </div>
            </div>
        </div>
    );
};


// ── Tire slot box in the diagram ──
function TireSlotBox({ slot, isAxleSelected, isCenter }: {
    slot: TireSlot;
    isAxleSelected: boolean;
    isCenter?: boolean;
}) {
    const def = TIRE_GRAPHICS[slot.tireGraphic];
    const hasAssigned = !!slot.assignedTireId;

    return (
        <div className={`ab-tire-slot ${hasAssigned ? 'assigned' : ''} ${isAxleSelected ? 'axle-selected' : ''} ${isCenter ? 'center' : ''}`}>
            <div className="ab-tire-graphic" style={{ width: Math.max(36, def.width * 0.7), height: Math.max(44, def.height * 0.7) }}>
                <div className="ab-tread-line" />
                <div className="ab-tread-line" />
                <div className="ab-tread-line" />
            </div>
            <span className="ab-slot-code">{slot.positionCode}</span>
            {slot.assignedTireId && (
                <span className="ab-assigned-id">{slot.assignedTireId}</span>
            )}
        </div>
    );
}

// ── Slot row in the controls panel ──
function SlotRow({ slot, index, axleId, onChangeGraphic }: {
    slot: TireSlot;
    index: number;
    axleId: string;
    onChangeGraphic: (axleId: string, slotId: string, graphic: TireGraphicType) => void;
}) {
    const currentCategory = TIRE_GRAPHICS[slot.tireGraphic]?.category || '';
    const categoryValues = Object.values(TIRE_CATEGORIES);

    const handleCategoryChange = (category: string) => {
        const match = Object.values(TIRE_GRAPHICS).find(g => g.category === category);
        if (match) onChangeGraphic(axleId, slot.id, match.type);
    };

    return (
        <div className="ab-slot-row">
            <span className="ab-slot-row-code">{slot.positionCode}</span>
            <span className="ab-slot-row-num">Slot {index}</span>
            <select
                className="ab-slot-select"
                value={currentCategory}
                onChange={e => handleCategoryChange(e.target.value)}
            >
                <option value="">Select...</option>
                {categoryValues.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                ))}
            </select>
        </div>
    );
}
